import React from 'react';
import { Document, Page, View, Text, Image, StyleSheet } from '@react-pdf/renderer';

// --- Utilitaires ---

function formatAmount(num) {
    if (num === null || num === undefined || num === '' || isNaN(num)) return '0,00';
    const n = parseFloat(num);
    const abs = Math.abs(n).toFixed(2);
    const parts = abs.split('.');
    const intPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return (n < 0 ? '-' : '') + intPart + ',' + parts[1];
}

function numberToFrenchWords(amount) {
    if (!amount || isNaN(amount)) { return 'Zéro euro.'; }

    const ones = [
        '', 'un', 'deux', 'trois', 'quatre', 'cinq', 'six', 'sept', 'huit', 'neuf',
        'dix', 'onze', 'douze', 'treize', 'quatorze', 'quinze', 'seize',
        'dix-sept', 'dix-huit', 'dix-neuf'
    ];
    const tens = ['', 'dix', 'vingt', 'trente', 'quarante', 'cinquante', 'soixante',
        'soixante', 'quatre-vingt', 'quatre-vingt'];

    function below1000(n) {
        if (n === 0) { return ''; }
        if (n < 20) { return ones[n]; }
        const t = Math.floor(n / 10);
        const o = n % 10;
        if (n < 100) {
            if (t === 7) { return 'soixante-' + ones[10 + o]; }
            if (t === 9) { return o === 0 ? 'quatre-vingt-dix' : 'quatre-vingt-' + ones[10 + o]; }
            if (o === 0) { return t === 8 ? 'quatre-vingts' : tens[t]; }
            if (o === 1 && t !== 8) { return tens[t] + ' et un'; }
            return tens[t] + '-' + ones[o];
        }
        const h = Math.floor(n / 100);
        const rem = n % 100;
        const centStr = h === 1 ? 'cent' : ones[h] + ' cent' + (rem === 0 && h > 1 ? 's' : '');
        return centStr + (rem > 0 ? ' ' + below1000(rem) : '');
    }

    function convertInt(n) {
        if (n === 0) { return 'zéro'; }
        let r = '';
        const billions  = Math.floor(n / 1000000000);
        const millions  = Math.floor((n % 1000000000) / 1000000);
        const thousands = Math.floor((n % 1000000) / 1000);
        const remainder = n % 1000;
        if (billions  > 0) { r += below1000(billions)  + ' milliard' + (billions  > 1 ? 's' : '') + ' '; }
        if (millions  > 0) { r += below1000(millions)  + ' million'  + (millions  > 1 ? 's' : '') + ' '; }
        if (thousands > 0) { r += (thousands === 1 ? '' : below1000(thousands) + ' ') + 'mille '; }
        if (remainder > 0) { r += below1000(remainder); }
        return r.trim();
    }

    const intPart = Math.floor(Math.abs(amount));
    const decPart = Math.round((Math.abs(amount) - intPart) * 100);

    let words = convertInt(intPart);
    words = words.charAt(0).toUpperCase() + words.slice(1);
    words += ' euro' + (intPart > 1 ? 's' : '');
    if (decPart > 0) {
        words += ' et ' + convertInt(decPart) + ' centime' + (decPart > 1 ? 's' : '');
    }
    return words + '.';
}

function ibanToRib(iban) {
    if (!iban) { return null; }
    const raw = iban.replace(/\s/g, '').toUpperCase();
    if (!raw.startsWith('FR') || raw.length < 27) { return null; }
    const rib = raw.slice(4);
    const bank    = rib.slice(0, 5);
    const branch  = rib.slice(5, 10);
    const account = rib.slice(10, 21);
    const key     = rib.slice(21, 23);
    return `${bank} ${branch} ${account} – ${key}`;
}

// --- Styles ---

const styles = StyleSheet.create({
    page: {
        fontFamily: 'Helvetica',
        fontSize: 10,
        paddingTop: 40,
        paddingBottom: 60,
        paddingHorizontal: 45,
        color: '#1a1a1a',
    },
    // Header
    header: {
        flexDirection: 'row',
        marginBottom: 28,
        alignItems: 'flex-start',
    },
    logoContainer: {
        width: 130,
        marginRight: 10,
    },
    logo: {
        maxWidth: 120,
        maxHeight: 120,
        objectFit: 'contain',
    },
    headerRight: {
        flex: 1,
        alignItems: 'flex-end',
        marginTop: 15,
    },
    clientName: {
        fontFamily: 'Helvetica-Bold',
        fontSize: 10,
        textAlign: 'left',
    },
    clientLine: {
        fontSize: 9,
        textAlign: 'left',
        marginTop: 5,
    },
    dateText: {
        fontSize: 10,
        marginTop: 20,
        textAlign: 'right',
    },
    // Convention
    conventionTitle: {
        fontFamily: 'Helvetica-Bold',
        fontSize: 10,
        marginBottom: 3,
    },
    factureNum: {
        fontSize: 9,
        color: '#CC0000',
        marginBottom: 14,
    },
    // Title box
    titleBox: {
        borderWidth: 1,
        borderColor: '#aaaaaa',
        backgroundColor: '#f7f7f7',
        paddingVertical: 10,
        paddingHorizontal: 16,
        alignItems: 'center',
        marginBottom: 18,
    },
    titleBoxText: {
        fontFamily: 'Helvetica-Bold',
        fontSize: 16,
        letterSpacing: 0.5,
        color: '#222222',
    },
    // Désignation
    sectionLabel: {
        textDecoration: 'underline',
        fontSize: 10,
        marginBottom: 6,
    },
    designationBlock: {
        marginBottom: 16,
    },
    designationLine: {
        fontFamily: 'Helvetica-Bold',
        fontSize: 10,
        lineHeight: 1.5,
    },
    yearLabel: {
        textDecoration: 'underline',
        fontSize: 10,
        marginBottom: 6,
    },
    capitalLine: {
        fontSize: 10,
        marginBottom: 3,
    },
    tauxLine: {
        fontSize: 10,
        marginBottom: 14,
    },
    // Tableau financier
    table: {
        borderWidth: 1,
        borderColor: '#aaaaaa',
        marginBottom: 14,
    },
    tableRowNormal: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderColor: '#aaaaaa',
    },
    tableRowTotal: {
        flexDirection: 'row',
        backgroundColor: '#eeeeee',
    },
    tableLabel: {
        flex: 1,
        padding: 7,
        fontSize: 10,
    },
    tableLabelBold: {
        flex: 1,
        padding: 7,
        fontSize: 10,
        fontFamily: 'Helvetica-Bold',
    },
    tableValue: {
        width: 160,
        padding: 7,
        fontSize: 10,
        textAlign: 'right',
        borderLeftWidth: 1,
        borderColor: '#aaaaaa',
    },
    tableValueBold: {
        width: 160,
        padding: 7,
        fontSize: 10,
        fontFamily: 'Helvetica-Bold',
        textAlign: 'right',
        borderLeftWidth: 1,
        borderColor: '#aaaaaa',
        color: '#CC0000',
    },
    // En lettre
    enLettreRow: {
        flexDirection: 'row',
        marginBottom: 20,
        flexWrap: 'wrap',
    },
    enLettreLabel: {
        fontSize: 10,
    },
    enLettreValue: {
        fontFamily: 'Helvetica-Bold',
        fontSize: 10,
        textDecoration: 'underline',
        flex: 1,
    },
    // Infos paiement
    paymentBox: {
        borderWidth: 1,
        borderColor: '#aaaaaa',
        padding: 10,
        marginBottom: 20,
        fontSize: 9,
        lineHeight: 1.6,
    },
    paymentHighlight: {
        color: '#CC0000',
    },
    // Signature
    signatureRow: {
        flexDirection: 'row',
        marginTop: 6,
    },
    signatureRight: {
        flex: 1,
        alignItems: 'center',
    },
    signatureTitle: {
        fontSize: 10,
        marginBottom: 8,
        textAlign: 'center',
    },
    tamponImage: {
        maxHeight: 70,
        objectFit: 'contain',
    },
    // Footer fixe
    footer: {
        position: 'absolute',
        bottom: 20,
        left: 45,
        right: 45,
        borderTopWidth: 1,
        borderColor: '#cccccc',
        paddingTop: 5,
        alignItems: 'center',
    },
    footerText: {
        fontSize: 7,
        color: '#666666',
        textAlign: 'center',
    },
});

// --- Composant principal ---

const FacturePortagePDF = ({
    op,
    annee,
    index,
    incidances,        // données incidences pour l'année sélectionnée
    incidancesPrev,    // données incidences pour l'année précédente (annee-1)
    organisme,         // { logo, tampon_signature, nom_organisme, iban, bic, num_voie, num_suffixe, type_voie, nom_voie, code_postale, ville }
    client,            // objet client complet (ou null)
    date,              // string de la date du jour
    numFacture,        // numéro de facture (identique au nom du fichier PDF)
    dateEcheance,      // DD/MM/YYYY = date d'édition + 30 jours
}) => {
    // Données financières
    const fraisHT   = parseFloat(incidances?.frais_portage_ht   || 0);
    const tvaFrais  = parseFloat(incidances?.tva_frais_portage  || 0);
    const fraisTTC  = parseFloat(incidances?.frais_portage_ttc  || 0);
    const tauxPct   = parseFloat(incidances?.taux_applicable_capital || 0); // en %
    const capitalN1 = parseFloat(incidancesPrev?.total_capital_porte || 0);

    // Fin de portage
    let finPortage = '';
    if (op.date_premiere_acquisition && op.duree_portage) {
        const acqYear = new Date(op.date_premiere_acquisition).getFullYear();
        finPortage = acqYear + parseInt(op.duree_portage);
    }

    // Ligne de désignation
    const designationParts = [];
    if (op.nom_client) {
        designationParts.push(op.nom_client);
    }
    if (op.nom_axe)
    {
        designationParts.push(`Secteur d'intervention – « ${op.nom} »`);
    }
    else {
        designationParts.push(`« ${op.nom} »`);
    }
    const portageLines = [
        op.nom_modalite,
        op.duree_portage ? `Portage foncier sur ${op.duree_portage} ans` : '',
        finPortage       ? `Fin de Portage en ${finPortage}` : '',
    ].filter(Boolean);
    const designation = designationParts.join(' – ');

    // Adresse client
    const buildClientLines = (c) => {
        if (!c)
        {
            return op.nom_client ? [op.nom_client] : [];
        }
        const lines = [];
        if (c.nom)
        {
            lines.push(c.nom);
        }
        if (c.nom_complement)
        {
            lines.push(c.nom_complement);
        }
        const street = [c.num_voie, c.num_suffixe, c.type_voie, c.nom_voie].filter(Boolean).join(' ');
        if (street)
        {
            lines.push(street);
        }
        if (c.complement_adresse)
        {
            lines.push(c.complement_adresse);
        }
        const city = [c.code_postale, c.ville ? c.ville.toUpperCase() : ''].filter(Boolean).join(' ');
        if (city)
        {
            lines.push(city);
        }
        return lines;
    };
    const clientLines = buildClientLines(client);

    // Adresse organisme (footer)
    const buildFooter = () => {
        const parts = [];
        if (organisme.nom_organisme) { parts.push(organisme.nom_organisme); }
        const street = [organisme.num_voie, organisme.num_suffixe, organisme.type_voie, organisme.nom_voie].filter(Boolean).join(' ');
        if (street) { parts.push(street); }
        if (organisme.complement_adresse) { parts.push(organisme.complement_adresse); }
        const city = [organisme.code_postale, organisme.ville].filter(Boolean).join(' ');
        if (city)   { parts.push(city); }
        if (organisme.telephone)    { parts.push(`Tél. : ${organisme.telephone}`); }
        if (organisme.email_contact) { parts.push(organisme.email_contact); }
        return parts.join(' – ');
    };

    // Ville pour la date
    const villeDate = organisme.ville
        ? `${organisme.ville.charAt(0).toUpperCase()}${organisme.ville.slice(1).toLowerCase()}, le ${date}`
        : date;

    return (
        <Document>
            <Page size="A4" style={styles.page}>

                {/* ---- En-tête ---- */}
                <View style={styles.header}>
                    <View style={styles.logoContainer}>
                        {organisme.logo ? (
                            <Image style={styles.logo} src={organisme.logo} />
                        ) : null}
                    </View>
                    <View style={styles.headerRight}>
                        {clientLines.map((line, i) => (
                            <Text key={i} style={i === 0 ? styles.clientName : styles.clientLine}>
                                {line}
                            </Text>
                        ))}
                        <Text style={styles.dateText}>{villeDate}</Text>
                    </View>
                </View>

                {/* ---- Titre convention + numéro facture ---- */}
                <Text style={styles.conventionTitle}>
                    {`CONVENTION DE PORTAGE « ${op.nom} »`}
                </Text>
                <Text style={styles.factureNum}>Facture n° {numFacture}</Text>

                {/* ---- Encadré titre ---- */}
                <View style={styles.titleBox}>
                    <Text style={styles.titleBoxText}>FACTURE DES FRAIS DE PORTAGE</Text>
                </View>

                {/* ---- Désignation ---- */}
                <Text style={styles.sectionLabel}>Désignation :</Text>
                <View style={styles.designationBlock}>
                    <Text style={styles.designationLine}>{designation}</Text>
                    {portageLines.map((line, i) => (
                        <Text key={i} style={styles.designationLine}>{line}</Text>
                    ))}
                </View>

                {/* ---- Année ---- */}
                <Text style={styles.yearLabel}>Année {annee} :</Text>
                <Text style={styles.capitalLine}>
                    {`Montant du capital porté restant dû au 31/12/${parseInt(annee) - 1} : ${formatAmount(capitalN1)} euros`}
                </Text>
                <Text style={styles.tauxLine}>
                    {`Taux de portage annuel ${formatAmount(tauxPct)}% HT`}
                </Text>

                {/* ---- Tableau ---- */}
                <View style={styles.table}>
                    <View style={styles.tableRowNormal}>
                        <Text style={styles.tableLabel}>Montant HT</Text>
                        <Text style={styles.tableValue}>{formatAmount(fraisHT)} euros</Text>
                    </View>
                    <View style={styles.tableRowNormal}>
                        <Text style={styles.tableLabel}>Montant TVA 20 %</Text>
                        <Text style={styles.tableValue}>{formatAmount(tvaFrais)} euros</Text>
                    </View>
                    <View style={styles.tableRowTotal}>
                        <Text style={styles.tableLabelBold}>Montant frais de portage TTC</Text>
                        <Text style={styles.tableValueBold}>{formatAmount(fraisTTC)} euros</Text>
                    </View>
                </View>

                {/* ---- En lettre ---- */}
                <View style={styles.enLettreRow}>
                    <Text style={styles.enLettreLabel}>En lettre : </Text>
                    <Text style={styles.enLettreValue}>{numberToFrenchWords(fraisTTC)}</Text>
                </View>

                {/* ---- Infos paiement ---- */}
                <View style={styles.paymentBox}>
                    <Text>Valeur en votre aimable règlement par virement</Text>
                    {ibanToRib(organisme.iban) ? (
                        <Text>
                            {'RIB : '}
                            <Text style={styles.paymentHighlight}>{ibanToRib(organisme.iban)}</Text>
                        </Text>
                    ) : null}
                    {organisme.iban ? (
                        <Text>
                            {'IBAN : '}
                            <Text style={styles.paymentHighlight}>{organisme.iban}</Text>
                        </Text>
                    ) : null}
                    {organisme.bic ? <Text>{`BIC : ${organisme.bic}`}</Text> : null}
                    <Text>
                        {'Référence à rappeler '}
                        <Text style={styles.paymentHighlight}>{numFacture}</Text>
                    </Text>
                    {dateEcheance ? (
                        <Text style={{marginTop: 6}}>
                            {`A compter du ${dateEcheance} des pénalités de retard de paiement seront appliquées (article 4.2.4 de notre règlement d'intervention).`}
                        </Text>
                    ) : null}
                </View>

                {/* ---- Signature ---- */}
                <View style={styles.signatureRow}>
                    <View style={{ flex: 1 }} />
                    <View style={styles.signatureRight}>
                        {organisme.tampon_signature ? (
                            <Image style={styles.tamponImage} src={organisme.tampon_signature} />
                        ) : null}
                    </View>
                </View>

                {/* ---- Footer fixe ---- */}
                <View style={styles.footer} fixed>
                    <Text style={styles.footerText}>{buildFooter()}</Text>
                    {organisme.pied_page ? (
                        <Text style={styles.footerText}>{organisme.pied_page}</Text>
                    ) : null}
                </View>

            </Page>
        </Document>
    );
};

export default FacturePortagePDF;
